import { call, put, takeLatest, takeEvery } from "redux-saga/effects";
import { toast } from "sonner";
import {
  createActs as createActsService,
  updateActs as updateActsService,
  deleteActs as deleteActsService,
  getActsById as getActsByIdService,
  filterActs as filterActsService,
  getActsPriceInsurerByInsurer as getActsPriceInsurerByInsurerService,
  createActsPriceInsurer as createActsPriceInsurerService,
  updateActsPriceInsurer as updateActsPriceInsurerService,
  deleteActsPriceInsurer as deleteActsPriceInsurerService,
} from "../../services/acts";
import * as types from "./types";
import { adaptResponseError } from "../../utils/utils";
import { getStorageInsurer } from "../../utils/storageUtils";

/**
 * Sagas du domaine Acts (Phase 2.F).
 *
 * <p>Conventions Phase 2.D appliquées :
 * <ul>
 *   <li>Tous les appels HTTP via {@code call(service, ...)} pour rester
 *       testables.</li>
 *   <li>Lecture de {@code result.ok} (plus jamais {@code result.success}).</li>
 *   <li>Pattern POST → Long → refetch : on lit l'id, on chaîne le
 *       refetch de la liste pour rafraîchir l'écran.</li>
 *   <li>Pas de token mémorisé au chargement du module — apiRequest gère
 *       l'auth via le storage à chaque requête.</li>
 *   <li>Lecture de {@code getStorageInsurer()} per-saga (pas au chargement
 *       du module) pour récupérer l'assureur courant à la volée.</li>
 *   <li>Toasts ici, jamais dans les composants.</li>
 * </ul>
 */

function* createActs({ payload }) {
  try {
    const result = yield call(createActsService, payload);
    if (!result?.ok) {
      yield put({
        type: types.CREATE_ACTS_FAILURE,
        payload: adaptResponseError(result || {}),
      });
      return;
    }
    toast.success("Acte créé avec succès");
    yield put({ type: types.CREATE_ACTS_SUCCESS, payload: { id: result.data } });
    // Rafraîchit la liste — réutilise le filterActs (alias rétro-compat).
    yield put({ type: types.FETCH_ACTS_REQUEST, payload: {} });
  } catch (error) {
    console.error("CreateActsSagaErr: ", error);
    yield put({
      type: types.CREATE_ACTS_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }],
    });
  }
}

function* updateActs({ payload }) {
  try {
    // Compat : payload peut être {data, id} (nouveau) ou un objet avec id direct (legacy).
    const id = payload?.id ?? payload?.data?.id;
    const data = payload?.data ?? (() => {
      const { id: _ignore, ...rest } = payload || {};
      return rest;
    })();
    if (id == null) {
      yield put({
        type: types.UPDATE_ACTS_FAILURE,
        payload: [{ message: "id manquant" }],
      });
      return;
    }
    const result = yield call(updateActsService, { data, id });
    if (!result?.ok) {
      yield put({
        type: types.UPDATE_ACTS_FAILURE,
        payload: adaptResponseError(result || {}),
      });
      return;
    }
    toast.success("Acte mis à jour");
    yield put({ type: types.UPDATE_ACTS_SUCCESS, payload: { id: result.data } });
    yield put({ type: types.FETCH_ACTS_REQUEST, payload: {} });
  } catch (error) {
    console.error("UpdateActsSagaErr: ", error);
    yield put({
      type: types.UPDATE_ACTS_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }],
    });
  }
}

function* fetchAllActs({ payload }) {
  try {
    const result = yield call(filterActsService, payload || {});
    if (!result?.ok) {
      yield put({
        type: types.FETCH_ACTS_FAILURE,
        payload: adaptResponseError(result || {}),
      });
      return;
    }
    yield put({ type: types.FETCH_ACTS_SUCCESS, payload: result.data });
  } catch (error) {
    console.error("FetchActsSagaErr: ", error);
    yield put({
      type: types.FETCH_ACTS_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }],
    });
  }
}

function* getActsById({ payload }) {
  try {
    const id = typeof payload === "object" && payload !== null ? payload?.id : payload;
    if (id == null) {
      yield put({
        type: types.GET_ACTS_BY_ID_FAILURE,
        payload: [{ message: "id manquant" }],
      });
      return;
    }
    const result = yield call(getActsByIdService, id);
    if (!result?.ok) {
      yield put({
        type: types.GET_ACTS_BY_ID_FAILURE,
        payload: adaptResponseError(result || {}),
      });
      return;
    }
    yield put({ type: types.GET_ACTS_BY_ID_SUCCESS, payload: result.data });
  } catch (error) {
    console.error("GetActsByIdSagaErr: ", error);
    yield put({
      type: types.GET_ACTS_BY_ID_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }],
    });
  }
}

function* deleteActs({ payload }) {
  try {
    // Compat : payload peut être l'id direct (cas {@code deleteAct(actId)}) ou un objet {id}.
    const id = typeof payload === "object" && payload !== null ? payload?.id : payload;
    if (id == null) {
      yield put({
        type: types.DELETE_ACTS_FAILURE,
        payload: [{ message: "id manquant" }],
      });
      return;
    }
    const result = yield call(deleteActsService, id);
    if (!result?.ok) {
      yield put({
        type: types.DELETE_ACTS_FAILURE,
        payload: adaptResponseError(result || {}),
      });
      return;
    }
    toast.success("Acte supprimé");
    yield put({ type: types.DELETE_ACTS_SUCCESS, payload: { id } });
    yield put({ type: types.FETCH_ACTS_REQUEST, payload: {} });
  } catch (error) {
    console.error("DeleteActsSagaErr: ", error);
    yield put({
      type: types.DELETE_ACTS_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }],
    });
  }
}

function* getActsPriceInsurerByInsurer({ payload: insurerId }) {
  try {
    const result = yield call(getActsPriceInsurerByInsurerService, insurerId);
    if (!result?.ok) {
      yield put({
        type: types.GET_ACTS_PRICE_INSURER_BY_INSURER_FAILURE,
        payload: [],
      });
      return;
    }
    yield put({
      type: types.GET_ACTS_PRICE_INSURER_BY_INSURER_SUCCESS,
      payload: result.data,
    });
  } catch (error) {
    console.error("GetActsPriceInsurerByInsurerSagaErr: ", error);
    yield put({
      type: types.GET_ACTS_PRICE_INSURER_BY_INSURER_FAILURE,
      payload: [],
    });
  }
}

function* refreshActsPriceInsurerForCurrentInsurer() {
  // Lit l'assureur courant depuis le storage à chaque dispatch (et pas au
  // chargement du module — sinon une déconnexion + reconnexion conserverait
  // le mauvais id).
  const storageInsurer = getStorageInsurer();
  const insurerId = storageInsurer?.id;
  if (insurerId != null) {
    yield put({
      type: types.GET_ACTS_PRICE_INSURER_BY_INSURER_REQUEST,
      payload: insurerId,
    });
  }
}

function* createActsPriceInsurer({ payload }) {
  try {
    const result = yield call(createActsPriceInsurerService, payload);
    if (!result?.ok) {
      yield put({
        type: types.CREATE_ACTS_PRICE_INSURER_FAILURE,
        payload: adaptResponseError(result || {}),
      });
      return;
    }
    toast.success("Tarif créé avec succès");
    yield put({
      type: types.CREATE_ACTS_PRICE_INSURER_SUCCESS,
      payload: { id: result.data },
    });
    yield call(refreshActsPriceInsurerForCurrentInsurer);
  } catch (error) {
    console.error("CreateActsPriceInsurerSagaErr: ", error);
    yield put({
      type: types.CREATE_ACTS_PRICE_INSURER_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }],
    });
  }
}

function* updateActsPriceInsurer({ payload }) {
  try {
    const result = yield call(updateActsPriceInsurerService, payload);
    if (!result?.ok) {
      yield put({
        type: types.UPDATE_ACTS_PRICE_INSURER_FAILURE,
        payload: adaptResponseError(result || {}),
      });
      return;
    }
    toast.success("Tarif mis à jour");
    yield put({
      type: types.UPDATE_ACTS_PRICE_INSURER_SUCCESS,
      payload: { id: result.data },
    });
    yield call(refreshActsPriceInsurerForCurrentInsurer);
  } catch (error) {
    console.error("UpdateActsPriceInsurerSagaErr: ", error);
    yield put({
      type: types.UPDATE_ACTS_PRICE_INSURER_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }],
    });
  }
}

function* deleteActsPriceInsurer({ payload }) {
  try {
    const result = yield call(deleteActsPriceInsurerService, payload);
    if (!result?.ok) {
      yield put({
        type: types.DELETE_ACTS_PRICE_INSURER_FAILURE,
        payload: adaptResponseError(result || {}),
      });
      return;
    }
    toast.success("Tarif supprimé");
    const id = typeof payload === "object" && payload !== null ? payload?.id : payload;
    yield put({
      type: types.DELETE_ACTS_PRICE_INSURER_SUCCESS,
      payload: { id },
    });
    yield call(refreshActsPriceInsurerForCurrentInsurer);
  } catch (error) {
    console.error("DeleteActsPriceInsurerSagaErr: ", error);
    yield put({
      type: types.DELETE_ACTS_PRICE_INSURER_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }],
    });
  }
}

export default function* ActsSaga() {
  yield takeLatest(types.CREATE_ACTS_REQUEST, createActs);
  yield takeLatest(types.UPDATE_ACTS_REQUEST, updateActs);
  yield takeLatest(types.FETCH_ACTS_REQUEST, fetchAllActs);
  yield takeEvery(types.GET_ACTS_BY_ID_REQUEST, getActsById);
  yield takeLatest(types.DELETE_ACTS_REQUEST, deleteActs);
  yield takeLatest(
    types.GET_ACTS_PRICE_INSURER_BY_INSURER_REQUEST,
    getActsPriceInsurerByInsurer
  );
  yield takeLatest(
    types.CREATE_ACTS_PRICE_INSURER_REQUEST,
    createActsPriceInsurer
  );
  yield takeLatest(
    types.UPDATE_ACTS_PRICE_INSURER_REQUEST,
    updateActsPriceInsurer
  );
  yield takeLatest(
    types.DELETE_ACTS_PRICE_INSURER_REQUEST,
    deleteActsPriceInsurer
  );
}
